import React, { useState } from 'react';
import { GlobeIcon, LockIcon, CalendarIcon, BuildingIcon, ClockIcon, FileTextIcon, EyeIcon, SendIcon, ClipboardListIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Column } from '../components/DataTable';
import { mockPublishedTenders, mockSupplierRFPs } from '../data/mockData';
import { PublishedTender, SupplierRFP } from '../types';
type MainTab = 'eoi' | 'rfp' | 'rfq';
type EOIFilter = 'All' | 'Open' | 'Closed' | 'Awarded';
interface SupplierRFQ {
  id: string;
  rfqRefNo: string;
  title: string;
  description: string;
  invitationDate: string;
  submissionDeadline: string;
  status: string;
  submittedDate?: string;
  quotedAmount?: number;
}
const mockSupplierRFQs: SupplierRFQ[] = [{
  id: '1',
  rfqRefNo: 'RFQ-2024-001',
  title: 'Office Furniture Supply — Desks & Chairs',
  description: 'You have been invited to submit a quotation for office furniture.',
  invitationDate: '2024-02-10',
  submissionDeadline: '2024-03-10',
  status: 'Pending Submission'
}, {
  id: '2',
  rfqRefNo: 'RFQ-2024-002',
  title: 'Networking Equipment — Switches & Routers',
  description: 'Submit your quotation for networking hardware procurement.',
  invitationDate: '2024-01-15',
  submissionDeadline: '2024-02-15',
  status: 'Submitted',
  submittedDate: '2024-02-10',
  quotedAmount: 45000
}, {
  id: '3',
  rfqRefNo: 'RFQ-2024-003',
  title: 'Printer & Copier Supplies — Annual Contract',
  description: 'Quotation invitation for printer toner and copier maintenance supplies.',
  invitationDate: '2024-02-20',
  submissionDeadline: '2024-03-20',
  status: 'Pending Submission'
}, {
  id: '4',
  rfqRefNo: 'RFQ-2023-008',
  title: 'Vehicle Spare Parts Supply',
  description: 'Your quotation for vehicle spare parts has been evaluated.',
  invitationDate: '2023-10-01',
  submissionDeadline: '2023-11-01',
  status: 'Awarded',
  submittedDate: '2023-10-25',
  quotedAmount: 28000
}, {
  id: '5',
  rfqRefNo: 'RFQ-2023-009',
  title: 'Electrical Supplies — Wiring & Fixtures',
  description: 'Your quotation was not selected for this procurement cycle.',
  invitationDate: '2023-09-15',
  submissionDeadline: '2023-10-15',
  status: 'Not Selected',
  submittedDate: '2023-10-08',
  quotedAmount: 15500
}];
export function TenderBoard() {
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState<MainTab>('eoi');
  const [eoiFilter, setEoiFilter] = useState<EOIFilter>('All');
  // Filter EOIs based on selected pill
  const filteredEOIs = eoiFilter === 'All' ? mockPublishedTenders : mockPublishedTenders.filter(t => t.status === eoiFilter);
  // EOI Status Badge Color
  const getEOIStatusColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-green-100 text-green-800';
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Awarded':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // EOI Category Badge Color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Goods':
        return 'bg-purple-100 text-purple-800';
      case 'Service':
        return 'bg-indigo-100 text-indigo-800';
      case 'Works':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };
  // RFP Status Badge Color
  const getRFPStatusColor = (status: string) => {
    switch (status) {
      case 'Pending Submission':
        return 'bg-yellow-100 text-yellow-800';
      case 'Submitted':
        return 'bg-blue-100 text-blue-800';
      case 'Under Evaluation':
        return 'bg-indigo-100 text-indigo-800';
      case 'Awarded':
        return 'bg-green-100 text-green-800';
      case 'Not Selected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRFQStatusColor = (status: string) => {
    switch (status) {
      case 'Pending Submission':
        return 'bg-yellow-100 text-yellow-800';
      case 'Submitted':
        return 'bg-blue-100 text-blue-800';
      case 'Under Evaluation':
        return 'bg-indigo-100 text-indigo-800';
      case 'Awarded':
        return 'bg-green-100 text-green-800';
      case 'Not Selected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const rfpColumns: Column<SupplierRFP>[] = [{
    key: 'rfpRefNo',
    label: 'RFP Ref No'
  }, {
    key: 'title',
    label: 'Title',
    render: item => <div className="max-w-xs truncate font-medium text-gray-900" title={item.title} data-id="element-2995">
          {item.title}
        </div>
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionDeadline',
    label: 'Deadline'
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRFPStatusColor(item.status)}`} data-id="element-2996">
          {item.status}
        </span>
  }, {
    key: 'quotedAmount',
    label: 'Quoted Amount',
    render: item => item.quotedAmount ? `$${item.quotedAmount.toLocaleString()}` : '—'
  }, {
    key: 'submittedDate',
    label: 'Submitted Date',
    render: item => item.submittedDate || '—'
  }];
  const rfqColumns: Column<SupplierRFQ>[] = [{
    key: 'rfqRefNo',
    label: 'RFQ Ref No'
  }, {
    key: 'title',
    label: 'Title',
    render: item => <div className="max-w-xs truncate font-medium text-gray-900" title={item.title} data-id="element-2997">
          {item.title}
        </div>
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionDeadline',
    label: 'Deadline'
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRFQStatusColor(item.status)}`} data-id="element-2998">
          {item.status}
        </span>
  }, {
    key: 'quotedAmount',
    label: 'Quoted Amount',
    render: item => item.quotedAmount ? `$${item.quotedAmount.toLocaleString()}` : '—'
  }, {
    key: 'submittedDate',
    label: 'Submitted Date',
    render: item => item.submittedDate || '—'
  }];
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2999">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3000">
        <div data-id="element-3001">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3002">Tender Board</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-3003">
            Browse open tenders and manage your RFP/RFQ submissions
          </p>
        </div>
        <div className="flex items-center bg-slate-50 px-4 py-2 rounded-md border border-slate-200" data-id="element-3004">
          <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2 animate-pulse" data-id="element-3005"></div>
          <div className="text-sm" data-id="element-3006">
            <span className="text-slate-500" data-id="element-3007">Logged in as: </span>
            <span className="font-medium text-slate-900" data-id="element-3008">
              TechCorp Solutions (SUP-001)
            </span>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="border-b border-gray-200" data-id="element-3009">
        <nav className="-mb-px flex space-x-8" data-id="element-3010">
          <button onClick={() => setActiveMainTab('eoi')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeMainTab === 'eoi' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3011">
            <GlobeIcon className="h-5 w-5 mr-2" data-id="element-3012" />
            Public EOI Listings
          </button>
          <button onClick={() => setActiveMainTab('rfp')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeMainTab === 'rfp' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3013">
            <LockIcon className="h-5 w-5 mr-2" data-id="element-3014" />
            My RFP Invitations
            <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${activeMainTab === 'rfp' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-3015">
              {mockSupplierRFPs.filter(r => r.status === 'Pending Submission').length}{' '}
              New
            </span>
          </button>
          <button onClick={() => setActiveMainTab('rfq')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeMainTab === 'rfq' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3016">
            <ClipboardListIcon className="h-5 w-5 mr-2" data-id="element-3017" />
            My RFQ Invitations
            <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${activeMainTab === 'rfq' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-3018">
              {mockSupplierRFQs.filter(r => r.status === 'Pending Submission').length}{' '}
              New
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6" data-id="element-3019">
        {activeMainTab === 'eoi' && <div className="space-y-6" data-id="element-3020">
            {/* EOI Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start" data-id="element-3021">
              <GlobeIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-3022" />
              <p className="text-sm text-blue-800" data-id="element-3023">
                These Expression of Interest notices are publicly available. Any
                qualified supplier may submit an EOI before the submission
                deadline.
              </p>
            </div>

            {/* EOI Filters */}
            <div className="flex flex-wrap gap-2" data-id="element-3024">
              {(['All', 'Open', 'Closed', 'Awarded'] as EOIFilter[]).map(filter => {
            const count = filter === 'All' ? mockPublishedTenders.length : mockPublishedTenders.filter(t => t.status === filter).length;
            return <button key={filter} onClick={() => setEoiFilter(filter)} className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${eoiFilter === filter ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-3025">
                      {filter}
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${eoiFilter === filter ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-3026">
                        {count}
                      </span>
                    </button>;
          })}
            </div>

            {/* EOI Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3027">
              {filteredEOIs.length > 0 ? filteredEOIs.map(tender => <div key={tender.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full overflow-hidden" data-id="element-3028">
                    <div className="p-5 flex-1 flex flex-col" data-id="element-3029">
                      <div className="flex justify-between items-start mb-3" data-id="element-3030">
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md" data-id="element-3031">
                          {tender.refNo}
                        </span>
                        <div className="flex gap-2" data-id="element-3032">
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(tender.category)}`} data-id="element-3033">
                            {tender.category}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getEOIStatusColor(tender.status)}`} data-id="element-3034">
                            {tender.status}
                          </span>
                        </div>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2" data-id="element-3035">
                        {tender.title}
                      </h3>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-1" data-id="element-3036">
                        {tender.description}
                      </p>

                      <div className="space-y-2 text-sm text-gray-600 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-3037">
                        <div className="flex items-center" data-id="element-3038">
                          <BuildingIcon className="h-4 w-4 mr-2 text-slate-400" data-id="element-3039" />
                          <span className="truncate" data-id="element-3040">{tender.department}</span>
                        </div>
                        <div className="flex items-center" data-id="element-3041">
                          <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" data-id="element-3042" />
                          <span data-id="element-3043">Published: {tender.publishDate}</span>
                        </div>
                        <div className="flex items-center text-indigo-700 font-medium" data-id="element-3044">
                          <ClockIcon className="h-4 w-4 mr-2 text-indigo-400" data-id="element-3045" />
                          <span data-id="element-3046">Deadline: {tender.submissionDeadline}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border-t border-gray-100 bg-gray-50" data-id="element-3047">
                      <button disabled={tender.status !== 'Open'} onClick={() => navigate(`/tender-board/eoi/${tender.id}/submit`)} className={`w-full flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors ${tender.status === 'Open' ? 'border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-50' : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'}`} data-id="element-3048">
                        {tender.status === 'Open' ? <>
                            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-3049" />
                            View Details & Submit
                          </> : 'Submission Closed'}
                      </button>
                    </div>
                  </div>) : <div className="col-span-full py-12 text-center bg-white rounded-lg border border-gray-200 border-dashed" data-id="element-3050">
                  <GlobeIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-3051" />
                  <p className="text-gray-500 font-medium" data-id="element-3052">
                    No tenders found for this filter.
                  </p>
                </div>}
            </div>
          </div>}

        {activeMainTab === 'rfp' && <div className="space-y-6" data-id="element-3053">
            {/* RFP Banner */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 flex items-start" data-id="element-3054">
              <LockIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-3055" />
              <p className="text-sm text-indigo-800" data-id="element-3056">
                These are Request for Proposal (RFP) invitations specifically
                assigned to your organization. Login is required to view and
                submit proposals.
              </p>
            </div>

            {/* RFP Data Table */}
            <div className="h-[600px]" data-id="element-3057">
              <DataTable columns={rfpColumns} data={mockSupplierRFPs} keyExtractor={item => item.id} searchPlaceholder="Search your RFPs..." actions={item => <div className="flex justify-end" data-id="element-3059">
                    {item.status === 'Pending Submission' ? <button onClick={() => navigate(`/tender-board/rfp/${item.id}/submit`)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-3060">
                        <SendIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-3061" />
                        Submit Proposal
                      </button> : <button onClick={() => navigate(`/tender-board/rfp/${item.id}/submit`)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-3062">
                        <EyeIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400" data-id="element-3063" />
                        View
                      </button>}
                  </div>} data-id="element-3058" />
            </div>
          </div>}

        {activeMainTab === 'rfq' && <div className="space-y-6" data-id="element-3064">
            {/* RFQ Banner */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 flex items-start" data-id="element-3065">
              <ClipboardListIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-3066" />
              <p className="text-sm text-indigo-800" data-id="element-3067">
                These are Request for Quotation (RFQ) invitations specifically
                assigned to your organization. Login is required to view and
                submit quotations.
              </p>
            </div>

            {/* RFQ Data Table */}
            <div className="h-[600px]" data-id="element-3068">
              <DataTable columns={rfqColumns} data={mockSupplierRFQs} keyExtractor={item => item.id} searchPlaceholder="Search your RFQs..." actions={item => <div className="flex justify-end" data-id="element-3070">
                    {item.status === 'Pending Submission' ? <button onClick={() => navigate(`/tender-board/rfq/${item.id}/submit`)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-3071">
                        <SendIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-3072" />
                        Submit Quotation
                      </button> : <button onClick={() => navigate(`/tender-board/rfq/${item.id}/submit`)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-3073">
                        <EyeIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400" data-id="element-3074" />
                        View
                      </button>}
                  </div>} data-id="element-3069" />
            </div>
          </div>}
      </div>
    </div>;
}